import { useMemo } from 'react';
import { useBrandingStore } from '../store/brandingStore';

const DEFAULT_PRIMARY_COLOR = '#e16349';
const DEFAULT_SECONDARY_COLOR = '#df1c41';

/**
 * Функция для затемнения цвета
 */
function darkenColor(color: string, percent: number): string {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
  const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
  const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

export interface BrandingColors {
  primary: string;
  primaryHover: string;
  ai: string;
  aiHover: string;
}

/**
 * Хук для получения цветов брендинга с мемоизацией
 * Автоматически вычисляет hover-цвета затемнением на 12%
 *
 * @param primaryColorOverride - Опциональный primary цвет (для публичных страниц, где цвет берётся из API)
 * @param secondaryColorOverride - Опциональный secondary цвет (для публичных страниц)
 *
 * Если параметры переданы - используются они (для публичных страниц с брендингом из API).
 * Если параметры не переданы - используются данные из brandingStore/localStorage (для авторизованных пользователей).
 */
export const useBrandingColors = (
  primaryColorOverride?: string | null,
  secondaryColorOverride?: string | null
): BrandingColors => {
  const { getPrimaryColor, getPrimaryHoverColor, getAiColor, getAiHoverColor, loaded, data } = useBrandingStore();

  return useMemo(() => {
    // Если переданы override-цвета (публичные страницы), используем их
    if (primaryColorOverride || secondaryColorOverride) {
      const primary = (primaryColorOverride && primaryColorOverride.trim()) || DEFAULT_PRIMARY_COLOR;
      const secondary = (secondaryColorOverride && secondaryColorOverride.trim()) || DEFAULT_SECONDARY_COLOR;

      return {
        primary,
        primaryHover: darkenColor(primary, 12),
        ai: secondary,
        aiHover: darkenColor(secondary, 12),
      };
    }

    // Если брендинг еще не загружен, возвращаем дефолтные цвета
    // но только если это не первая загрузка (чтобы избежать мигания)
    if (!loaded || !data) {
      // Проверяем, есть ли данные в localStorage для немедленного применения
      const storedData = localStorage.getItem('branding:v1');
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          if (parsed.primaryColor) {
            return {
              primary: parsed.primaryColor,
              primaryHover: getPrimaryHoverColor(),
              ai: parsed.secondaryColor || DEFAULT_SECONDARY_COLOR,
              aiHover: getAiHoverColor(),
            };
          }
        } catch (e) {
          // Игнорируем ошибки парсинга
        }
      }

      // Если нет данных в localStorage, возвращаем дефолтные цвета
      return {
        primary: DEFAULT_PRIMARY_COLOR,
        primaryHover: darkenColor(DEFAULT_PRIMARY_COLOR, 12),
        ai: DEFAULT_SECONDARY_COLOR,
        aiHover: darkenColor(DEFAULT_SECONDARY_COLOR, 12),
      };
    }

    return {
      primary: getPrimaryColor(),
      primaryHover: getPrimaryHoverColor(),
      ai: getAiColor(),
      aiHover: getAiHoverColor(),
    };
  }, [primaryColorOverride, secondaryColorOverride, getPrimaryColor, getPrimaryHoverColor, getAiColor, getAiHoverColor, loaded, data]);
};

/**
 * Типы для вариантов кнопок
 */
export type ButtonVariant = 'primary' | 'ai' | 'secondary';

/**
 * Утилита для получения стилей кнопок
 */
export const getButtonStyles = (colors: ReturnType<typeof useBrandingColors>, variant: ButtonVariant) => {
  const baseStyles = {
    transition: 'colors 0.2s ease-in-out',
    cursor: 'pointer',
  };

  switch (variant) {
    case 'primary':
      return {
        ...baseStyles,
        backgroundColor: colors.primary,
        color: '#ffffff',
        '&:hover': {
          backgroundColor: colors.primaryHover,
        },
      };
    
    case 'ai':
      return {
        ...baseStyles,
        background: `linear-gradient(to right, ${colors.ai}, ${colors.primary})`,
        color: '#ffffff',
        '&:hover': {
          background: `linear-gradient(to right, ${colors.aiHover}, ${colors.primaryHover})`,
        },
      };
    
    case 'secondary':
      return {
        ...baseStyles,
        backgroundColor: '#f8f9fa',
        color: '#525866',
        border: '1px solid #e2e4e9',
        '&:hover': {
          backgroundColor: '#f0f1f2',
        },
      };
    
    default:
      return baseStyles;
  }
};

/**
 * Утилита для получения inline-стилей кнопки
 */
export const getButtonInlineStyles = (colors: ReturnType<typeof useBrandingColors>, variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: colors.primary,
        color: '#ffffff',
      };
    
    case 'ai':
      return {
        background: `linear-gradient(to right, ${colors.ai}, ${colors.primary})`,
        color: '#ffffff',
      };
    
    case 'secondary':
      return {
        backgroundColor: '#f8f9fa',
        color: '#525866',
        border: '1px solid #e2e4e9',
      };
    
    default:
      return {};
  }
};

/**
 * Утилита для получения hover-стилей кнопки
 */
export const getButtonHoverStyles = (colors: ReturnType<typeof useBrandingColors>, variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: colors.primaryHover,
        color: '#ffffff',
      };
    
    case 'ai':
      return {
        background: `linear-gradient(to right, ${colors.aiHover}, ${colors.primaryHover})`,
        color: '#ffffff',
      };
    
    case 'secondary':
      return {
        backgroundColor: '#f0f1f2',
        color: '#525866',
      };
    
    default:
      return {};
  }
};
